import { React, useState, useEffect, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import RDataGrid from "../reusables/RDataGrid";
import PageLoader from "../loader/PageLoader";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import AddPopup from "../popup/AddPopup";
import ReorderIcon from "@mui/icons-material/Reorder";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Add from "@mui/icons-material/Add";
import Button from "@mui/joy/Button";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import AddSupplier from "../forms/AddSupplier";
import { LightTooltip } from "../tooltip/ToolTip";
import AddPaymentMode from "../forms/AddPaymentMode";

const PaymentModes = () => {
  const [paymentModes, setPaymentModes] = useState([]);
  const [isFetchingPaymentModes, setIsFetchingPaymentModes] = useState(true);
  const [editPaymentMode, setEditPaymentMode] = useState(false);
  const [viewPaymentMode, setViewPaymentMode] = useState(false);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshPaymentModes, setSetRefreshPaymentModes] = useState(null);

  const PAYMENT_MODES_URL = "api/v1/payment-modes";
  const axiosPrivate = useAxiosPrivate();

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const onDelete = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      const response = await axiosPrivate.delete(PAYMENT_MODES_URL + "/" + id, {
        signal: controller.signal,
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setSetRefreshPaymentModes(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Payment Mode",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onDelete(id);
        },
      });
    }
  };

  useEffect(() => {
    if (!isFetchingPaymentModes) {
      setIsFetchingPaymentModes(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getPaymentModes = async () => {
      try {
        //const response = await axiosPrivate.get(BAG_URL, {
        const response = await axiosPrivate.get(
          PAYMENT_MODES_URL,
          {
            signal: controller.signal,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        isMounted && setPaymentModes(response.data.data);
        setIsFetchingPaymentModes(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingPaymentModes(false);
      }
    };

    getPaymentModes();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshPaymentModes]);

  const columns = useMemo(
    () => [
      {
        field: "rowNo", // from DataGrid rows defifnition
        headerName: "No.",
        sortable: false,
        headerClassName: "super-app-theme--header",
        minWidth: 87,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "name",
        headerName: "Name",
        headerClassName: "super-app-theme--header",
        minWidth: 160,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        headerClassName: "super-app-theme--header",
        align: "center",
        headerAlign: "center",
        minWidth: 120,
        flex: 1,
        sortable: false,
        disableClickEventBubbling: true,
        cellClassName: "actions",
        getActions: (params) => {
          return [
            <LightTooltip title="Edit payment mode">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={function () {
                  const currentRow = params.row;
                  const selectedPaymentMode = JSON.stringify(
                    currentRow,
                    null,
                    4
                  );
                  setViewPaymentMode(false);
                  setEditPaymentMode(true);
                  setSelectedPaymentMode(selectedPaymentMode);
                  return setOpenPopup(true);
                }}
                color="success"
              />
            </LightTooltip>,
            <LightTooltip title="Delete payment mode">
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                color="error"
                onClick={function () {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Are you sure you want to delete this payment mode?",
                    subTitle: "You can't undo this operation",
                    action: "Delete",
                    onConfirm: () => {
                      onDelete(params.row.id);
                    },
                  });
                }}
              />
            </LightTooltip>,
            <GridActionsCellItem
              icon={<ReorderIcon />}
              label={"View Payment Mode"}
              onClick={function () {
                const currentRow = params.row;
                const selectedPaymentMode = JSON.stringify(currentRow, null, 4);
                setViewPaymentMode(true);
                setEditPaymentMode(true); // You might want to edit payment mode under view payment mode
                setSelectedPaymentMode(selectedPaymentMode);
                return setOpenPopup(true);
              }}
              color="inherit"
              showInMenu
            />,
          ];
        },
      },
    ],
    []
  );

  // Close dialog upon successful payment mode add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setSetRefreshPaymentModes(new Date());
  };

  return isFetchingPaymentModes ? (
    <PageLoader title="Loading payment modes..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      {paymentModes?.length ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              sx={{
                textAlign: "left",
                mt: 3,
                mb: 3,
                fontSize: {
                  lg: 30,
                  md: 20,
                  sm: 20,
                  xs: 19,
                },
              }}
            >
              Payment Modes
            </Typography>

            <Button
              sx={{
                size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
              }}
              variant="solid"
              color="primary"
              onClick={function () {
                setEditPaymentMode(false);
                setSelectedPaymentMode(null);
                setOpenPopup(true);
              }}
              startDecorator={<Add />}
            >
              Payment Mode
            </Button>
          </div>
          <Box
            sx={{
              width: "100%",
              margin: "0 auto 16px",
              height: 560,
              overflow: "hidden",
            }}
          >
            <RDataGrid columns={columns} rows={paymentModes} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewPaymentMode
                ? "View (Edit) Payment Mode"
                : editPaymentMode
                ? "Edit (View) Payment Mode"
                : "Add Payment Mode"
            }
          >
            <AddPaymentMode
              closeDialog={closeDialog}
              editPaymentMode={editPaymentMode}
              selectedPaymentMode={selectedPaymentMode}
            />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No payment modes to display
          </Typography>
          <Button
            sx={{
              size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
            }}
            variant="solid"
            color="primary"
            onClick={function () {
              setEditPaymentMode(false);
              setSelectedPaymentMode(null);
              setOpenPopup(true);
            }}
            startDecorator={<Add />}
          >
            Payment Mode
          </Button>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewPaymentMode
                ? "View (Edit) Payment Mode"
                : editPaymentMode
                ? "Edit (View) Payment Mode"
                : "Add Payment Mode"
            }
          >
            <AddPaymentMode
              closeDialog={closeDialog}
              editPaymentMode={editPaymentMode}
              selectedPaymentMode={selectedPaymentMode}
            />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default PaymentModes;
